"use client";

import { useState, useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function ManufacturingHero() {
  const [isZh, setIsZh] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
    }
  }, []);
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.15
  });
  const paragraph = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.15
  });
  const ctaRow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const imageBlock = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const baseTransition = "transition-all duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]";
  return <section data-section-id="20045" id="global-toy-manufacturing-excellence" className="relative bg-background text-foreground py-32 md:py-44 overflow-hidden">
      <div className="mx-auto w-full max-w-[1240px] px-8 md:px-14 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div ref={eyebrow.ref} className={`${baseTransition} ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "0ms"
          }}>
              <span className="font-mono text-xs font-medium uppercase text-foreground/60 tracking-[0.18em]">
                {isZh ? "全球 OEM/ODM 解決方案" : "Global OEM/ODM Solutions"}
              </span>
            </div>

            <h1 ref={heading.ref} className={`mt-6 font-default font-bold text-foreground ${baseTransition} ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(2.75rem, 5.4vw, 4.75rem)",
            letterSpacing: "-0.03em",
            lineHeight: "1.1",
            transitionDelay: "80ms"
          }}>
              {isZh ? <>
                  為全球品牌{" "}
                  <span className="relative inline-block italic">
                    精準製造
                    <span aria-hidden="true" className={`absolute left-0 right-0 block h-px bg-[var(--chart-1)] transition-transform duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] origin-left ${heading.isVisible ? "scale-x-100" : "scale-x-0"}`} style={{
                  bottom: "-0.18em",
                  transitionDelay: "800ms"
                }} />
                  </span>
                </> : <>
                  Manufacturing{" "}
                  <span className="relative inline-block italic">
                    precision
                    <span aria-hidden="true" className={`absolute left-0 right-0 block h-px bg-[var(--chart-1)] transition-transform duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] origin-left ${heading.isVisible ? "scale-x-100" : "scale-x-0"}`} style={{
                  bottom: "-0.18em",
                  transitionDelay: "800ms"
                }} />
                  </span>{" "}
                  for global brands
                </>}
            </h1>

            <p ref={paragraph.ref} className={`mt-8 max-w-[520px] font-default text-foreground/75 ${baseTransition} ${paragraph.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
            lineHeight: "1.6",
            letterSpacing: "-0.002em",
            transitionDelay: "160ms"
          }}>
              {isZh ? "Elite Group Corporation Limited 提供全方位端到端 OEM/ODM 服務。從研發及產品開發，到大規模工廠生產與嚴格的國際合規測試，我們為您的品牌締造卓越品質。" : "Elite Group Corporation Limited provides end-to-end OEM/ODM services. From R&D and product development to high-capacity factory production and rigorous international compliance testing, we deliver excellence for your brand."}
            </p>

            <div ref={ctaRow.ref} className={`mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8 ${baseTransition} ${ctaRow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "240ms"
          }}>
              <Button asChild className="h-[52px] bg-[var(--chart-1)] text-foreground hover:bg-[var(--chart-1)]/90 px-7 font-default font-semibold tracking-[0.01em] transition-colors duration-200">
                <Link to="#">
                  {isZh ? "查看產品目錄" : "View our catalog"}
                </Link>
              </Button>

              <Link to="#" className="group inline-flex items-center gap-2 font-default font-semibold text-foreground">
                <span className="relative">
                  {isZh ? "聯絡我們團隊" : "Contact our team"}
                  <span aria-hidden="true" className="absolute left-0 right-0 -bottom-1 h-px bg-[var(--chart-1)] transition-all duration-200 group-hover:h-0.5" />
                </span>
                <ArrowRight className="size-4 text-[var(--chart-1)] transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
            </div>
          </div>

          <div ref={imageBlock.ref} className={`lg:col-span-5 ${baseTransition} ${imageBlock.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: "320ms"
        }}>
            <div className="relative w-full overflow-hidden border border-foreground/10">
              <div className="aspect-[4/5] w-full">
                <img src="https://joint-gopher.10web.cloud/wp-content/uploads/2026/07/Shopify-Product-Photo-1.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707169/img/factory_production_floor.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707169/img/factory_production_floor.webp-wvc-sizes" alt={isZh ? "展示精密組裝工藝的現代化製造廠房" : "Modern manufacturing facility floor showing precision assembly processes"} loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </div>
            </div>
            <p className="mt-4 font-default text-xs font-medium text-foreground/60 tracking-[0.04em]">
              {isZh ? "ISO 認證生產基地" : "ISO-certified production facilities"}
            </p>
          </div>
        </div>
      </div>
    </section>;
}